<template>
  <DefaultPanel
    :status="hasError ? 'warning' : ''"
    :empty="annotationsCount === 0 && !canEdit"
  >
    <template #header>
      <span class="icon">
        <i v-if="isLoading" class="fa-solid fa-spinner spinner fa-xs" />
        <i v-else class="fa-solid fa-tags fa-xs" />
      </span>
      <span>Annotations</span>
      <span class="tag tag-count is-rounded is-primary">
        {{ annotationsCount }}
      </span>
    </template>
    <template v-if="canEdit" #toolbar>
      <fieldset :disabled="isLoading" style="display: contents">
        <div class="new-annotation-form p-0">
          <SelectContainer
            ref="annotationTypeSelect"
            placeholder="Pick an annotation type to add"
            :value="newAnnotationType"
            :options="annotationOptions"
            @input="(annotType) => handleSetAnnotationType(annotType)"
          />
          <div
            v-if="newAnnotationType.id"
            class="field has-addons annotation-value-row mt-1"
          >
            <div class="control is-expanded">
              <FieldComponent
                ref="annotationValueField"
                :key="newAnnotationType.id"
                :field-meta="newAnnotationFieldMeta"
                :data="newAnnotationValue"
                :is-edit-mode="canEdit"
                validation-scope="new-annotation-add"
                @edit="handleSetAnnotationValue"
                @keyup.enter="handleFormAddSingleAnnotation"
                @close="focusAddButton"
              />
            </div>
            <div class="control">
              <button
                ref="addAnnotationButton"
                :disabled="
                  !hasNewValue || isAddingAnnotation || newAnnotationIsInvalid
                "
                class="button is-primary"
                :class="{
                  'is-outlined':
                    !hasNewValue ||
                    isAddingAnnotation ||
                    newAnnotationIsInvalid,
                }"
                @click="handleFormAddSingleAnnotation"
              >
                <span class="icon">
                  <i class="fa-solid fa-plus-circle" />
                </span>
                <span>Add</span>
              </button>
            </div>
          </div>
        </div>
      </fieldset>
    </template>
    <div v-if="annotationsCount > 0">
      <fieldset :disabled="isLoading" style="display: contents">
        <ol class="form-fields">
          <li
            v-for="annotation of sortedAnnotations"
            :key="`${annotation.id || annotation._tempId}`"
            class="field"
            :class="{ editing: isEditing }"
          >
            <AnnotationListItem
              :ref="`blinkable-${annotation.id}`"
              :annotation="{
                ...annotation,
                value: edits[annotation.name] || annotation.value,
              }"
              :has-edits="!!edits[annotation.name]"
              :field-meta="{
                ...annotationMetaModels[annotation.name],
                ...annotationMetaDetailViews[annotation.name],
              }"
              :is-editing="isEditing"
              @edit="(value) => handleEdit(annotation.name, value)"
              @save="handleSaveEdits(annotation.name)"
              @cancel="handleCancelEdits(annotation.name)"
              @delete="handleDelete(annotation.name)"
              @keyup.enter="() => handleSaveEdits()"
            />
          </li>
        </ol>
        <div class="is-flex is-justify-content-flex-end">
          <button
            v-if="isEditing && hasEdits"
            class="button is-small is-fullwidth mr-2"
            :disabled="isSavingEdits"
            @click="() => handleCancelEdits()"
          >
            Cancel
          </button>
          <button
            v-if="isEditing && hasEdits"
            class="button is-primary is-small is-fullwidth has-text-weight-bold"
            :disabled="isSavingEdits"
            @click="() => handleSaveEdits()"
          >
            <span class="icon">
              <i class="fa-solid fa-save" />
            </span>
            <span>Save</span>
            <span class="pl-1">({{ editsCount }})</span>
          </button>
        </div>
      </fieldset>
    </div>
  </DefaultPanel>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { v4 as uuid } from 'uuid'
import { computed, nextTick, ref, toRef, watch } from 'vue'
import { useRoute } from 'vue-router'

import { del, patch, post } from '@/api'
import { useStore } from '@/store'
import { fetchRecordAnnotations } from '@/utils/api/annotations'

import AnnotationListItem from '@/components/AnnotationListItem.vue'
import DefaultPanel from '@/components/DefaultPanel.vue'
import SelectContainer from '@/components/SelectContainer.vue'

export default {
  name: 'AnnotationList',
  components: {
    AnnotationListItem,
    SelectContainer,
    DefaultPanel,
  },
  props: {
    appName: {
      type: String,
      required: true,
    },
    modelName: {
      type: String,
      required: true,
    },
    recordId: {
      type: String,
      default: null,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    loadFrom: {
      type: Object,
      default: null,
    },
  },
  emits: ['delete'],
  setup(props, { emit }) {
    const store = useStore()
    const route = useRoute()
    const modelName = computed(() => props.modelName || route.params?.modelName)
    const id = computed(() => props.recordId || route.params.id)
    const baseUrl = computed(
      () => `${props.appName}/${modelName.value}/${id.value}/annotations/`,
    )
    const hasError = ref(false)

    const isMetaReady = computed(
      () => store.getters['meta/isAnnotationMetalModelReady'],
    )
    const annotationMetaModels = computed(() => {
      const amm = store.getters['meta/annotationMetaModels']
      return amm.reduce((acc, obj) => ({ ...acc, [obj.name]: obj }), {})
    })
    const annotationMetaViews = computed(() => {
      const amm = store.getters['meta/annotationMetaViews']
      return amm.reduce((acc, obj) => ({ ...acc, [obj.name]: obj }), {})
    })
    const annotationMetaDetailViews = computed(() => {
      const amm = store.getters['meta/getAnnotationMetaDetails']
      return amm.reduce((acc, obj) => ({ ...acc, [obj.name]: obj }), {})
    })

    const annotationList = ref([])
    const isFetchingAnnots = ref(false)
    async function fetchAnnotations() {
      let annots = []
      isFetchingAnnots.value = true
      annots = await fetchRecordAnnotations({
        appName: props.appName,
        modelName: modelName.value,
        id: id.value,
      })
      isFetchingAnnots.value = false
      return annots
    }
    const recordAnnotations = ref({})
    watch(
      () => annotationList.value,
      (aa) =>
        aa.forEach((a) => {
          const label = annotationMetaModels.value[a.name]?.label
          recordAnnotations.value[a.name] = { ...a, label }
        }),
    )
    function sortAnnotations(annots) {
      return [...annots].sort((a, b) => {
        const aLabel = annotationMetaViews.value[a.name]?.label || a.name
        const bLabel = annotationMetaViews.value[b.name]?.label || b.name
        return aLabel.localeCompare(bLabel)
      })
    }
    const sortedAnnotations = computed(() => {
      return sortAnnotations(Object.values(recordAnnotations.value))
    })
    const annotationsCount = computed(
      () => sortedAnnotations.value?.length || 0,
    )
    const annotationOptions = computed(() => {
      // Remove annotation types from options when they are already present on item
      return Object.values(annotationMetaModels.value).filter(
        (m) => !(m.name in recordAnnotations.value) && !m.readonly,
      )
    })

    // ADD SINGLE ANNOTATION
    const newAnnotationType = ref({})
    const newAnnotationValue = ref({})

    const newAnnotationFieldMeta = computed(() => ({
      ...annotationMetaModels.value[newAnnotationType.value.name],
      ...annotationMetaDetailViews.value[newAnnotationType.value.name],
    }))

    // Collect validation state from the annotation value FieldComponent.
    // The FieldComponent registers under this scope via the :validation-scope prop.
    const v$NewAnnotation = useVuelidate(
      {},
      {},
      { $scope: 'new-annotation-add' },
    )
    const newAnnotationIsInvalid = computed(
      () => v$NewAnnotation.value.$invalid,
    )

    function handleSetAnnotationType(t) {
      newAnnotationValue.value = {}
      newAnnotationType.value = t || {}
      focusAnnotationValueField()
    }
    const annotationValueField = ref(null)
    async function focusAnnotationValueField() {
      await nextTick()
      annotationValueField.value?.$el?.querySelector('input')?.focus()
    }
    const addAnnotationButton = ref(null)
    async function focusAddButton() {
      await nextTick()
      const isMultiple =
        annotationMetaViews.value[newAnnotationType.value.name]?.multiple
      if (isMultiple) focusAnnotationValueField()
      else addAnnotationButton.value.focus()
    }
    function handleSetAnnotationValue(value) {
      newAnnotationValue.value = { value }
    }
    const hasNewValue = computed(() => {
      const v = newAnnotationValue.value
      return newAnnotationType.value.id && v.value ? true : false
    })

    const newAnnotation = computed(() => ({
      _tempId: uuid(),
      annotation_type_id: newAnnotationType.value.id,
      name: newAnnotationType.value.name,
      value: newAnnotationValue.value.value,
    }))

    const isAddingAnnotation = ref(false)
    const justAddedAnnotIds = ref({})

    async function addAnnotation(
      annotation = { annotation_type_id: null, value: null },
    ) {
      try {
        isAddingAnnotation.value = true
        const response = await post(baseUrl.value, {
          data: annotation,
        })
        if (response.status >= 200 && response.status < 300) {
          const a = response.data
          recordAnnotations.value[a.name] = a
          justAddedAnnotIds.value[a.id] = true
          resetAnnotationForm()
        } else throw new Error()
      } catch (error) {
        const message = error.response.data.message
        store.commit('notify', { message, type: 'error' })
      } finally {
        isAddingAnnotation.value = false
      }
    }

    async function handleAddAnnotation() {
      await addAnnotation(newAnnotation.value)
    }
    function resetAnnotationForm() {
      newAnnotationType.value = {}
      newAnnotationValue.value = {}
    }
    const annotationTypeSelect = ref(null)
    async function handleFormAddSingleAnnotation() {
      if (!hasNewValue.value) return
      await handleAddAnnotation(newAnnotation.value)
      resetAnnotationForm()
      // auto reopen dropdown when done adding an annot
      // might be an annoying behavior... will maybe remove
      // annotationTypeSelect.value.$el.querySelector('input').focus()
    }

    // CLONE ANNOT
    const isCloningAnnotations = ref(false)
    const loadFrom = toRef(props, 'loadFrom')
    const hasLoadedFromSource = ref(null)
    async function handleLoadAnnotations() {
      // Needed when cloning parent item
      if (!loadFrom.value) return

      // Check if we've already loaded from this source
      // This prevents re-loading when navigating away and back
      const sourceId = loadFrom.value.id
      if (hasLoadedFromSource.value === sourceId) {
        return // Already loaded, skip
      }

      try {
        isCloningAnnotations.value = true
        const aa = await fetchRecordAnnotations({
          appName: props.appName,
          modelName: modelName.value,
          ...loadFrom.value,
        })
        const annotationsToAdd = aa
          .filter(({ name }) => !annotationMetaViews.value[name]?.readonly)
          .filter(({ name }) => !recordAnnotations.value[name])

        if (annotationsToAdd.length > 0) {
          await Promise.all(
            annotationsToAdd.map((a) =>
              this.addAnnotation({
                _tempId: uuid(),
                annotation_type_id: a.annotation_type_id,
                name: a.name,
                value: a.value,
              }),
            ),
          )
        }

        // Mark as loaded to prevent re-loading
        hasLoadedFromSource.value = sourceId
        isCloningAnnotations.value = false
      } catch (error) {
        const message =
          error.response?.data?.message || 'Error: could not clone annotations'
        store.commit('notify', { message, type: 'error' })
        isCloningAnnotations.value = false
      }
    }

    // EDIT
    const edits = ref({})
    const editsCount = computed(() => Object.keys(edits.value).length || 0)
    const hasEdits = computed(() => editsCount.value > 0)
    const isSavingEdits = ref(false)
    const editPayloadAnnots = computed(() =>
      Object.entries(edits.value)
        .filter(([, v]) => v)
        .map(([k, v]) => ({ ...recordAnnotations.value[k], value: v })),
    )

    function handleEdit(annotationName, newValue) {
      edits.value[annotationName] = newValue
    }
    function onEditSuccess(newAnnotations) {
      newAnnotations.forEach((a) => {
        delete edits.value[a.name]
        recordAnnotations.value[a.name] = a
      })
    }
    async function handleSaveEdits(name = null) {
      isSavingEdits.value = true
      try {
        let payload = editPayloadAnnots.value
        if (name) payload = payload.filter((a) => a.name === name)
        const response = await patch(baseUrl.value, {
          data: { results: payload },
        })
        if (response.status >= 200 && response.status < 300) {
          const editedAnnots = response.data.results
          onEditSuccess(editedAnnots)
          if (editedAnnots?.length === 0) initialise()
          this.blinkEdited()
          // this.resetEdits()
        } else throw new Error()
      } catch (error) {
        const message = error.response.data.message
        store.commit('notify', { type: 'error', message })
      } finally {
        isSavingEdits.value = false
      }
    }
    function handleCancelEdits(name = null) {
      if (name) delete edits.value[name]
      else edits.value = {}
    }

    // DELETE SINGLE ANNOTATION
    const deletingAnnotsIds = ref({})
    const justDeletedAnnotIds = ref({})

    function onAnnotDeleteSuccess(a) {
      delete recordAnnotations.value[a.name]
      // add to an object for displaying notifs
      justDeletedAnnotIds.value[a.id] = true
      delete edits.value[a.name]
      // + timeout function to remove from this object
      emit('delete', a.annotation_type_id)
    }
    async function handleDelete(annotationName) {
      const a = recordAnnotations.value[annotationName]
      try {
        deletingAnnotsIds.value[a.id] = true
        const response = await del(`${baseUrl.value}${a.id}/`)
        if (response.status >= 200 && response.status < 300) {
          onAnnotDeleteSuccess(a)
        } else throw new Error()
      } catch {
        const message = `Error: could not delete annotation ${a.id}`
        store.commit('notify', { message, type: 'error' })
      } finally {
        delete deletingAnnotsIds.value[a.id]
      }
    }
    const isInitialising = ref(true)
    const isLoading = computed(
      () =>
        !isMetaReady.value ||
        isInitialising.value ||
        isFetchingAnnots.value ||
        Object.keys(deletingAnnotsIds.value).lenght > 0,
    )
    async function initialise() {
      isInitialising.value = true
      edits.value = {}
      annotationList.value = await fetchAnnotations()
      // also fetch clone annots if props.loadFrom
      isInitialising.value = false
    }
    watch(
      () => isMetaReady.value,
      (r) => r && initialise(),
      { immediate: true },
    )

    return {
      isMetaReady,
      id,
      isLoading,
      annotationMetaModels,
      annotationMetaViews,
      annotationMetaDetailViews,
      annotationOptions,
      recordAnnotations,
      sortedAnnotations,
      annotationsCount,
      hasError,
      // ADD
      newAnnotationType,
      newAnnotationValue,
      newAnnotationFieldMeta,
      newAnnotationIsInvalid,
      hasNewValue,
      handleSetAnnotationType,
      annotationValueField,
      handleSetAnnotationValue,
      addAnnotationButton,
      focusAddButton,
      addAnnotation,
      handleAddAnnotation,
      annotationTypeSelect,
      handleFormAddSingleAnnotation,
      isAddingAnnotation,
      justAddedAnnotIds,
      // CLONE ANNOTS
      handleLoadAnnotations,
      // EDIT
      edits,
      editsCount,
      hasEdits,
      editPayloadAnnots,
      handleEdit,
      handleSaveEdits,
      handleCancelEdits,
      isSavingEdits,
      onEditSuccess,
      // DEL
      handleDelete,
      deletingAnnotsIds,
      justDeletedAnnotIds,
    }
  },
  methods: {
    // blinkAdded() {
    //   Object.keys(this.edits).forEach((id) => {
    //     this.$refs[`blinkable-${id}`][0].$el.classList.add('edited')
    //     setTimeout(() => {
    //       this.$refs[`blinkable-${id}`][0].$el.classList.remove('edited')
    //     }, 1000)
    //   })
    // },
    // blinkEdited() {
    //   Object.keys(this.edits).forEach((id) => {
    //     this.$refs[`blinkable-${id}`][0].$el.classList.add('edited')
    //     setTimeout(() => {
    //       this.$refs[`blinkable-${id}`][0].$el.classList.remove('edited')
    //     }, 1000)
    //   })
    // },
  },
  async mounted() {
    if (this.loadFrom) await this.handleLoadAnnotations()
  },
}
</script>

<style lang="scss" scoped>
ol.form-fields {
  list-style-type: none;
  padding: 0;

  > .field {
    margin-top: 0;
  }
}

.annotation-value-row {
  align-items: flex-start;

  // FieldComponent renders a div.field-resolver, not a native input, so
  // Bulma's built-in has-addons selectors don't reach the actual input.
  // Apply the radius/border joins manually.
  .control:first-child:not(:only-child) {
    margin-right: -1px;
    :deep(input, .multiselect__tags) {
      border-bottom-right-radius: 0;
      border-top-right-radius: 0;
    }
    :deep(input:hover, .multiselect:hover) {
      z-index: 2;
    }
  }
}

.edited {
  animation: blink 0.5s 1 alternate;
}

@keyframes blink {
  from {
    background-color: var(--blue-500);
  }
  to {
    background-color: none;
  }
}
</style>
